import classNames from 'classnames'
import React, { useCallback, useEffect, type FormEvent, type ReactNode } from 'react'
import { useBeforeUnload, useNavigate, useSearchParams } from 'react-router-dom'

import { useBookcafeData } from '../../module/myPage/hook'
import { usePostSurvey } from '../../module/sconnStudyData/hook'
import { useSurvey, useSurveySteps } from '../../module/survey/hook'
import { type SurveyStep } from '../../module/survey/types'
import { useErrorToast } from '../../util/hooks/useToast'

const modifySearchParams = (currentStep: SurveyStep, searchParams: URLSearchParams) => {
  if (currentStep === 'classroom') {
    searchParams.delete('classroom')
  }
  if (currentStep === 'examinations' || currentStep === 'examinationsCollege') {
    searchParams.delete('exam')
    searchParams.delete('hasUserSearch')
    searchParams.delete('isUniversity')
  }
  if (currentStep === 'year') {
    searchParams.delete('year')
  }
}

export default function Form({
  children,
  onFormLoad,
  onSubmitSuccess,
}: {
  children: ReactNode
  onFormLoad?: () => void
  onSubmitSuccess?: () => void
}) {
  const { user } = useBookcafeData()
  const { mutate, isPending } = usePostSurvey()
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    surveyOnModal,
    selectedExamItems,
    selectedUserGroup,
    selectedUserGroupGrade,
    setSurveyIsPending,
  } = useSurvey()
  const { setErrorMessage, setOpenError, errorToast } = useErrorToast()
  const { steps, activeStep, setSkipFirstStep } = useSurveySteps()
  const currentStep = steps[activeStep]
  const navigate = useNavigate()

  useEffect(() => {
    modifySearchParams(currentStep, searchParams)
    setSearchParams(searchParams)
  }, [])

  useEffect(() => {
    if (!user?.suggestNickName || user?.email !== user?.nickName) {
      setSkipFirstStep(true)

      if (onFormLoad) {
        onFormLoad()
      }
    }
  }, [user?.suggestNickName, user?.nickName])

  useEffect(() => {
    setSurveyIsPending(isPending)
  }, [isPending])

  useBeforeUnload(
    useCallback((e) => {
      e.preventDefault()
      e.returnValue = ''
    }, []),
  )

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    // V2 제출 페이로드: 키워드 선택은 id로, 검색/직접입력 시험은 이름으로 전달
    if (selectedUserGroup === null) {
      return
    }

    const examKeywordIds = selectedExamItems
      .map((item) => (item.source === 'keyword' ? item.keywordId : undefined))
      .filter((keywordId): keywordId is number => keywordId !== undefined)
    const examCustomNames = selectedExamItems
      .filter((item) => item.source !== 'keyword')
      .map((item) => item.label)

    mutate(
      {
        examKeywordIds,
        examCustomNames,
        userGroup: selectedUserGroup,
        ...(selectedUserGroupGrade !== null && { userGroupGrade: selectedUserGroupGrade }),
      },
      {
        onSuccess: (data) => {
          if (data.result === 'Success') {
            if (onSubmitSuccess) {
              onSubmitSuccess()
            } else {
              navigate('complete')
            }
          }
        },
        onError: (err) => {
          setOpenError(true)
          setErrorMessage(`설문 제출에 실패했습니다. \n[${err.message}]`)
        },
      },
    )
  }

  return (
    <form
      id='survey-form'
      className={classNames(
        'flex flex-col h-full',
        surveyOnModal
          ? 'ml-[1px] mr-[6px]' /* margin for prevent clipping box-shadow/close-icon by overflow-auto */
          : 'ml-[26px] mr-[25px] tablet-sm:mx-[78px] tablet-md:mx-[80px] tablet-lg:mx-[118px]',
      )}
      onSubmit={handleSubmit}
    >
      {children}
      {errorToast()}
    </form>
  )
}
